import { logError } from "../components/logger";
import { get } from "../lib/api";
import { ClinicIds } from "../store/ClinicStore";
import { AppointmentType, AppointmentTypeSchema } from "../types/appointmenttype";
import { ServiceType, ServiceTypeSchema } from "../types/servicetype";
import { ServiceTypeWithAppointmentTypes } from "../types/servicetypewithappointmenttypes";
import { gqlRequest } from "../utils/GraphQLRequest";

export const getServiceTypesForClinic = async ({ groupCRM, userFK, locationId }: ClinicIds): Promise<ServiceType[]> => {
  return getServiceTypesForClinicLegacy({ groupCRM, userFK, locationId });
}

export const getServiceTypesForClinicLegacy = async ({ groupCRM, userFK, locationId }: ClinicIds): Promise<ServiceType[]> => {
    // Ensure we're working with plain values by destructuring
    const plainClinicIds = { groupCRM, userFK, locationId };
    
    const res = await gqlRequest(
        process.env.REACT_APP_BASE_URL,
        `
        query ServiceTypes(
            $input: ServiceTypesInput!
            $appointmentTypesFilters: AppointmentTypesFilters
            $serviceTypesFilters: ServiceTypesFilters
        ) {
            serviceTypes(
                input: $input
                appointmentTypesFilters: $appointmentTypesFilters
                serviceTypesFilters: $serviceTypesFilters
            ) {
                ...ServiceType
            }
        }

        fragment ServiceType on ServiceType {
            id
            name
        }
        `,
        {
            input: {
                clinic_group_crm: plainClinicIds.groupCRM,
                user_fk: plainClinicIds.userFK,
                location_id: plainClinicIds.locationId,
            },
        }
    );
    
    // Check if serviceTypes is null and return an empty array if so
    if (!res.data.serviceTypes) {
        // console.log('No service types found for the given clinic parameters');
        return [];
    }
    
    const serviceTypes = ServiceTypeSchema.array().parse(res.data.serviceTypes);
    return serviceTypes;
}

export const getAppointmentTypesForServiceType = async ({ groupCRM, userFK, locationId }: ClinicIds, serviceTypeId: string): Promise<AppointmentType[]> => {
  return getAppointmentTypesForServiceTypeLegacy({ groupCRM, userFK, locationId }, serviceTypeId);
}

export const getAppointmentTypesForServiceTypeLegacy = async ({ groupCRM, userFK, locationId }: ClinicIds, serviceTypeId: string): Promise<AppointmentType[]> => {
    try {
        // Ensure we're working with plain values by destructuring
        const plainClinicIds = { groupCRM, userFK, locationId };
        
        const res = await gqlRequest(
            process.env.REACT_APP_BASE_URL,
            `
            query AppointmentTypes(
                $input: AppointmentTypesInput!
                $filters: AppointmentTypesFilters
            ) {
                appointmentTypes(input: $input, filters: $filters) {
                    ...AppointmentType
                }
            }
            fragment AppointmentType on AppointmentType {
                clinic_group_crm
                user_fk
                location_id
                service_type_id
                appointment_type_id
                display_name
                standard_duration
                is_face_to_face
                start_time
                end_time
                nurse_required
                is_default
                cancellation_period
                question_required
                continue_or_no
                appt_que
                appt_inst
                new_patient
                week_days_only
                week_end_only
                pre_payment_req
                pre_payment_fees
                pre_payment_msg
                pre_payment_cnfrm_msg
                post_payment_req
                post_billing_type
                post_billing_fees
                post_billing_msg
                post_billing_cnfrm_msg
                consent_enabled
                consent_id
                low_age_min
                low_age_max
                upper_age_min
                upper_age_max
                last_f2f_visit_months
            }
            `,
            {
                input: {
                    clinic_group_crm: plainClinicIds.groupCRM,
                    user_fk: plainClinicIds.userFK,
                    location_id: plainClinicIds.locationId,
                    service_type_id: serviceTypeId,
                },
            }
        );
        
        // Check if appointmentTypes is null and return an empty array if so
        if (!res.data.appointmentTypes) {
            console.log(`No appointment types found for service type ${serviceTypeId}`);
            return [];
        }
        
        // Parse the appointment types if they exist
        const appointmentTypes = AppointmentTypeSchema.array().parse(res.data.appointmentTypes);
        return appointmentTypes;
    } catch (error) {
        logError('getAppointmentTypesForServiceType', error);
        console.error('Error fetching appointment types:', error);
        // Return empty array instead of throwing to make function more resilient
        return [];
    }
}

export const getAllServiceTypesWithAppointmentTypes = async (clinicIds: ClinicIds): Promise<ServiceTypeWithAppointmentTypes[]> => {
    const allServiceTypes: ServiceTypeWithAppointmentTypes[] = [];
    let serviceTypes = await getServiceTypesForClinic(clinicIds);
    if (serviceTypes.length === 0) {
        console.log("No service types available for the given clinic parameters");
        return []; // Return empty array instead of throwing error
    }
    
    for (const serviceType of serviceTypes) {
        try {
            const appointmentTypes = await getAppointmentTypesForServiceType(clinicIds, serviceType.id);
            allServiceTypes.push({
                ...serviceType,
                appointment_types: appointmentTypes
            });
        } catch (error) {
            logError('getAllServiceTypesWithAppointmentTypes', error);
            console.error(`Error fetching appointment types for service type ${serviceType.id}:`, error);
            // Continue with next service type instead of failing the whole function
            allServiceTypes.push({
                ...serviceType,
                appointment_types: []
            });
        }
    }
    return allServiceTypes;
}

export const getDefaultAppointmentType = (serviceTypesWithAppointmentTypes: ServiceTypeWithAppointmentTypes[]): AppointmentType | null => {
    // Return null if there are no service types
    if (!serviceTypesWithAppointmentTypes || serviceTypesWithAppointmentTypes.length === 0) {
        console.log("No service types provided");
        return null;
    }
    
    try {
        // Look for default appointment type
        for (const serviceType of serviceTypesWithAppointmentTypes) {
            const defaultAppointmentType = serviceType.appointment_types.find(
              (appointmentType) => appointmentType.is_default === "1"
            );
            if (defaultAppointmentType) {
            //   console.log("Found default appointment type");
              return defaultAppointmentType;
            }
        }
        
        // Look for GP service type
        console.log("No default flag on appointment type found, checking for GP service type");
        const gpServiceType = serviceTypesWithAppointmentTypes.find(
            (serviceType) => serviceType.id === "10"
        ); // 10 is the id for the General Practitioner service type
        if (gpServiceType) {
            // Look for standard appointment in GP service type
            const standardAppointment = gpServiceType.appointment_types.find(
              (appointmentType) => appointmentType.display_name.includes("Standard")
            );
            if (standardAppointment) {
              console.log("Found appointment with 'standard' in the name");
              return standardAppointment;
            }
            
            // Return first appointment type in GP service type
            console.log("No standard appointment found in GP, selecting first in GP if any");
            if (gpServiceType.appointment_types.length > 0) {
              return gpServiceType.appointment_types[0];
            }
        }
        
        // Return first appointment type in any service type
        console.log("No GP service type or no appointment types in GP, selecting first service type with first appointment type");
        for (const serviceType of serviceTypesWithAppointmentTypes) {
            if (serviceType.appointment_types.length > 0) {
              return serviceType.appointment_types[0];
            }
        }
        
        // No appointment types found
        console.log("No appointment types found in any service type");
        return null;
    } catch (error) {
        logError('getDefaultAppointmentType', error);
        console.error("Error finding default appointment type:", error);
        return null;
    }
}

export const getAppointmentTypesForClinic = async (groupCRM: string, userFK: string, locationId: string): Promise<AppointmentType[]> => {
    const appointmentData = await get(`clinics/appointmenttypes?clinic_group_crm=${groupCRM}&user_fk=${userFK}&location_id=${locationId}&show_all=1`);
    return AppointmentTypeSchema.array().parse(appointmentData);
}


export const getDoctorsAvailability = async (groupCRM: any, userFK: string, locationId: string, doctorId: string, startDate: string, endDate: string, appointmentTypeId: string): Promise<AppointmentType[]> => {
    const doctorsAvailability = await get(`/doctors/availability?clinic_group_crm=${groupCRM}&user_fk=${userFK}&location_id=${locationId}&doctor_id=${doctorId}&start_date=${startDate}&end_date=${endDate}&appointment_type_id=${appointmentTypeId}`);
    return (doctorsAvailability) as any[];
}